import { css } from '@emotion/react';
import { Sentiment } from '@weave/schema-gen-ts/dist/shared/call-intelligence/enums.pb';
import { NotificationType } from '@weave/schema-gen-ts/dist/shared/notification/notifications.pb';
import dayjs from 'dayjs';
import { IntelCard } from '@frontend/call-intel-insights';
import { useTranslation } from '@frontend/i18n';
import { getFullName } from '@frontend/name';
import { formatPhoneNumber } from '@frontend/phone-numbers';
import type { GetWeavePopNotificationByType, NotificationActions } from '@frontend/types';
import { theme } from '@frontend/theme';
import { Text, styles as dsStyles } from '@frontend/design-system';
import { useNotificationContext } from '../notification-provider';
import { BaseNotificationComponentProps, BaseNotificationInner, BaseNotificationOuterNew } from './base';

type Props = BaseNotificationComponentProps<GetWeavePopNotificationByType<'unhappy-call'>>;

export const UnhappyCallNotificationComponent = ({ isHistorical, emit, notification, ...rest }: Props) => {
  const { t } = useTranslation();

  const { callId, categories, startTime, person, phoneNumber, locationId } = notification.payload;

  const notificationTrayContext = useNotificationContext();

  const markRead = () =>
    notificationTrayContext.markCallIntelNotificationsAsRead(
      callId ?? '',
      NotificationType.CALL_INTELLIGENCE_UNHAPPY_CALL,
      locationId ?? ''
    );

  const actions: NotificationActions = [
    {
      label: t('View'),
      action: () => {
        emit({ action: 'view', payload: undefined }, notification);
        markRead();
      },
    },
  ];

  return (
    <BaseNotificationOuterNew
      actions={actions}
      notification={notification}
      emit={emit}
      isHistorical={isHistorical}
      {...rest}
    >
      <BaseNotificationInner
        onClose={() => emit({ action: 'ignore', payload: undefined }, notification)}
        actions={actions}
        title={t('Call Analyzed by Call Intelligence')}
        body={
          <section
            css={css`
              display: flex;
              flex-direction: column;
              align-items: flex-start;
              gap: ${theme.spacing(1)};
              p {
                margin: 0;
              }
            `}
          >
            <Text size='medium'>{t('We think you should review this')}</Text>
            <IntelCard.Attributes>
              <IntelCard.Sentiment sentiment={Sentiment.SENTIMENT_NEGATIVE} />
              <IntelCard.Category category={categories?.[0]} />
            </IntelCard.Attributes>
            <div
              css={css`
                display: flex;
                flex-direction: column;
                gap: ${theme.spacing(0.5)};
                width: 100%;
              `}
            >
              <Text weight='bold' size='medium' css={dsStyles.truncate}>
                {getFullName({
                  firstName: person?.firstName,
                  lastName: person?.lastName,
                  preferredName: person?.preferredName,
                }) || formatPhoneNumber(phoneNumber?.nationalNumber?.toString() ?? '')}
              </Text>
              <Text size='medium'>{dayjs(startTime).format('MMM DD YYYY, hh:mm A')}</Text>
            </div>
          </section>
        }
        location={notification.location}
        timestamp={notification.timestamp}
        id={notification.id}
        notificationType={notification.type}
        {...rest}
      />
    </BaseNotificationOuterNew>
  );
};
